import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import React, { useEffect, useState } from "react";
import { ProgressSpinner } from "primereact/progressspinner";
import {
  genElDate,
  genElNumber,
  genElObject,
  genElText,
} from "@components/utils/EditorDialogElementGenerator";
import DepartmentSelectorDialog from "../../../dialogs/selectors/DepartmentSelectorDialog";
import JenisMinyakSelectorDialog from "../../../dialogs/selectors/JenisMinyakSelectorDialog";
import AlatBeratSelectorDialog from "../../../dialogs/selectors/AlatBeratSelectorDialog";
import KaryawanSelectorDialog from "../../../dialogs/selectors/KaryawanSelectorDialog";
import {
  buildKeteranganStokAwal,
  useAPIEditKartuStokMinyak,
} from "../api/api-edit-kartu-stok-minyak";

const KartuStokMinyakEdit = (props) => {
  const { visible, onHide, editorData, onConfirmed } = props;

  const [showDepartmentSelector, setShowDepartmentSelector] = useState(false);
  const [showJenisMinyakSelector, setShowJenisMinyakSelector] = useState(false);
  const [showAlatBeratSelector, setShowAlatBeratSelector] = useState(false);
  const [showOperatorSelector, setShowOperatorSelector] = useState(false);

  const {
    loading,
    data,
    setData,
    setError,
    error,
    saldo,
    isMasuk,
    isAwal,
    selisihAwal,
    fetchSaldo,
    fetchDataOrCreateNew,
    handleOnConfirm,
  } = useAPIEditKartuStokMinyak({ onConfirmed });

  // use effect section

  useEffect(() => {
    if (visible) {
      setError({});
      fetchDataOrCreateNew({ id: editorData.id, extraData: editorData });
    }
  }, [visible]);

  // the balance of the chosen department + jenis minyak
  useEffect(() => {
    if (visible) {
      fetchSaldo({
        department_id: data.department_id,
        jenis_minyak_id: data.jenis_minyak_id,
      });
    }
  }, [visible, data.department_id, data.jenis_minyak_id]);

  // stok awal of an empty month: fill the keterangan with the counted stock and last month's sisa,
  // until the user writes their own text
  useEffect(() => {
    if (!visible || !isAwal || !data.sheet_kosong) return;

    const otomatis = buildKeteranganStokAwal({
      tanggal_waktu: data.tanggal_waktu,
      stok_fisik: data.stok_fisik,
      saldo,
    });
    if (!otomatis) return;

    setData((prevData) =>
      !prevData.keterangan || prevData.keterangan === prevData.keterangan_otomatis
        ? { ...prevData, keterangan: otomatis, keterangan_otomatis: otomatis }
        : prevData
    );
  }, [visible, isAwal, data.sheet_kosong, data.tanggal_waktu, data.stok_fisik, saldo]);

  // handle ui event section

  const handleOnConfirmDepartment = (event, datas) => {
    if (datas && datas.length > 0) {
      const department = datas[0];
      setData((prevData) => ({
        ...prevData,
        data_department: department,
        department_id: department.id,
        // the alat berat belongs to a department
        data_alat_berat: null,
        alat_berat_id: null,
      }));
      setShowDepartmentSelector(false);
    }
  };

  const handleOnConfirmJenisMinyak = (event, datas) => {
    if (datas && datas.length > 0) {
      const jenisMinyak = datas[0];
      setData((prevData) => ({
        ...prevData,
        data_jenis_minyak: jenisMinyak,
        jenis_minyak_id: jenisMinyak.id,
      }));
      setShowJenisMinyakSelector(false);
    }
  };

  const handleOnConfirmAlatBerat = (event, datas) => {
    if (datas && datas.length > 0) {
      const alatBerat = datas[0];
      setData((prevData) => ({
        ...prevData,
        data_alat_berat: alatBerat,
        alat_berat_id: alatBerat.id,
        // default the jenis minyak to the one of the equipment
        ...(alatBerat.jenis_minyak_id && !prevData.jenis_minyak_id
          ? {
              data_jenis_minyak: alatBerat.data_jenis_minyak,
              jenis_minyak_id: alatBerat.jenis_minyak_id,
            }
          : {}),
      }));
      setShowAlatBeratSelector(false);
    }
  };

  const handleOnConfirmOperator = (event, datas) => {
    if (datas && datas.length > 0) {
      const operator = datas[0];
      setData((prevData) => ({
        ...prevData,
        data_operator: operator,
        operator_id: operator.id,
        operator_nama: operator.nama,
      }));
      setShowOperatorSelector(false);
    }
  };

  // genereate editor element section

  const elDepartment = genElObject({
    field: "department_id",
    name: "Department",
    value: data.data_department ? data.data_department.nama : "",
    error,
    onClick: () => setShowDepartmentSelector(true),
  });

  const elJenisMinyak = genElObject({
    field: "jenis_minyak_id",
    name: "Jenis Minyak",
    value: data.data_jenis_minyak ? data.data_jenis_minyak.nama : "",
    error,
    onClick: () => setShowJenisMinyakSelector(true),
  });

  const elTanggal = genElDate({
    field: "tanggal_waktu",
    name: "Tanggal / Waktu",
    data,
    setData,
    error,
    showTime: true,
    primeFormat: "dd MM yy",
  });

  const elAlatBerat = genElObject({
    field: "alat_berat_id",
    name: "Alat Berat",
    value: data.data_alat_berat
      ? [data.data_alat_berat.kode_unit, data.data_alat_berat.nama]
          .filter(Boolean)
          .join(" - ")
      : "",
    error,
    disabled: !data.department_id,
    onClick: () => setShowAlatBeratSelector(true),
  });

  const elOperator = genElObject({
    field: "operator_id",
    name: "Operator (Karyawan)",
    value: data.data_operator?.nama ?? data.operator_nama ?? "",
    error,
    onClick: () => setShowOperatorSelector(true),
    onClear: () =>
      setData((prevData) => ({
        ...prevData,
        data_operator: null,
        operator_id: null,
        operator_nama: "",
      })),
  });

  const elStokFisik = genElNumber({
    field: "stok_fisik",
    name: "Stok Fisik (Liter)",
    data,
    setData,
    error,
    showButtons: true,
    min: 0,
    min_fraction_digits: 0,
    max_fraction_digits: 2,
  });

  const elMasuk = genElNumber({
    field: "masuk",
    name: "Volume Masuk (Liter)",
    data,
    setData,
    error,
    showButtons: true,
    min: 0,
    min_fraction_digits: 0,
    max_fraction_digits: 2,
  });

  const elKeluar = genElNumber({
    field: "keluar",
    name: "Volume Keluar (Liter)",
    data,
    setData,
    error,
    showButtons: true,
    min: 0,
    min_fraction_digits: 0,
    max_fraction_digits: 2,
  });

  const elKeterangan = genElText({
    field: "keterangan",
    name: "Keterangan",
    data,
    setData,
    error,
  });

  const volume = isMasuk ? data.masuk : data.keluar;
  const sisaSetelah = Number(saldo ?? 0) + (isMasuk ? 1 : -1) * Number(volume ?? 0);

  const dialogFooter = (
    <React.Fragment>
      <Button
        label="Cancel"
        icon="pi pi-times"
        outlined
        onClick={props.onHide}
      />
      <Button
        label="Simpan"
        icon="pi pi-check"
        onClick={(e) => {
          handleOnConfirm();
        }}
      />
    </React.Fragment>
  );

  return (
    <Dialog
      visible={visible}
      style={{ width: "32rem" }}
      breakpoints={{ "960px": "75vw", "641px": "92vw" }}
      header={
        isAwal
          ? "Catat Stok Minyak"
          : `${data.id && data.id > 0 ? "Edit" : "Catat"} ${
              isMasuk ? "Stok Masuk Minyak" : "Pengeluaran Minyak"
            }`
      }
      modal
      className={`p-fluid`}
      footer={dialogFooter}
      onHide={onHide}
      contentClassName="py-4 !pb-6"
      pt={{ footer: { className: "py-4" }, header: { className: "py-4" } }}
    >
      <DepartmentSelectorDialog
        title={"Pilih Department"}
        visible={showDepartmentSelector}
        onHide={() => setShowDepartmentSelector(false)}
        onConfirm={handleOnConfirmDepartment}
      />

      <JenisMinyakSelectorDialog
        title={"Pilih Jenis Minyak"}
        visible={showJenisMinyakSelector}
        onHide={() => setShowJenisMinyakSelector(false)}
        onConfirm={handleOnConfirmJenisMinyak}
      />

      <AlatBeratSelectorDialog
        title={"Pilih Alat Berat"}
        visible={showAlatBeratSelector}
        onHide={() => setShowAlatBeratSelector(false)}
        onConfirm={handleOnConfirmAlatBerat}
        additionalFilter={
          data.department_id ? `department_id:=:${data.department_id}` : undefined
        }
      />

      <KaryawanSelectorDialog
        title={"Pilih Operator"}
        visible={showOperatorSelector}
        onHide={() => setShowOperatorSelector(false)}
        onConfirm={handleOnConfirmOperator}
      />

      <div className="col-start-center relative h-100 gap-3">
        {elDepartment}
        {elJenisMinyak}
        {saldo !== null && (
          <div className="row-between-center w-full rounded bg-emerald-50 px-3 py-2">
            <span className="text-sm font-semibold text-slate-500">
              {isAwal ? "Sisa di sistem" : "Sisa stok saat ini"}
            </span>
            <span className="font-bold text-emerald-600">
              {Number(saldo).toLocaleString("id-ID")} L
            </span>
          </div>
        )}
        {elTanggal}
        {isAwal ? (
          <>
            <span className="w-full text-sm text-slate-400">
              Isi stok hasil cek fisik. Hanya selisih dengan sisa di sistem yang dicatat.
            </span>
            {elStokFisik}
            {selisihAwal !== null && Number(data.stok_fisik) >= 0 && (
              <span
                className={`w-full text-sm ${
                  selisihAwal === 0 ? "text-slate-400" : "text-amber-600"
                }`}
              >
                {selisihAwal === 0
                  ? data.sheet_kosong
                    ? "Sama dengan sisa bulan lalu, dicatat sebagai stok awal bulan ini."
                    : "Sama dengan sisa di sistem, tidak ada selisih."
                  : `Selisih ${selisihAwal > 0 ? "+" : "-"}${Math.abs(
                      selisihAwal
                    ).toLocaleString("id-ID", { maximumFractionDigits: 2 })} L, dicatat sebagai ${
                      selisihAwal > 0 ? "stok masuk" : "pengeluaran"
                    } penyesuaian.`}
              </span>
            )}
          </>
        ) : isMasuk ? (
          <>
            <span className="w-full text-sm text-slate-400">
              Stok yang datang dicatat tanpa alat berat dan operator.
            </span>
            {elMasuk}
          </>
        ) : (
          <>
            {elAlatBerat}
            {elOperator}
            {elKeluar}
          </>
        )}
        {!isAwal && saldo !== null && !(data.id > 0) && Number(volume) > 0 && (
          <span
            className={`w-full text-sm ${
              sisaSetelah < 0 ? "text-red-500" : "text-slate-400"
            }`}
          >
            Sisa setelah ini{" "}
            {sisaSetelah.toLocaleString("id-ID", { maximumFractionDigits: 2 })} L
          </span>
        )}
        {elKeterangan}
        {loading && (
          <div
            className={`col-center-center w-100  gap-2 absolute z-100 h-100 bg-black/20 rounded `}
          >
            <ProgressSpinner />
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default KartuStokMinyakEdit;
